/**
 * One box's statement (كشف حساب): every movement over a period with the
 * running balance, opening and closing figures at either end. Supplier
 * payments made from the Suppliers screen appear here too.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import { FilterDates } from "components/FilterBar/controls";
import DialogShell from "./DialogShell";
import { api, problemOf, money } from "./api";

const KIND = {
  receipt: "Receipt",
  payment: "Payment",
  transfer: "Transfer",
  supplier: "Supplier payment",
};

function StatementDialog({ box, onClose }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!box) return;
    setData(null);
    setError("");
    api
      .get(`/boxes/${box.id}/statement`, { from: from || undefined, to: to || undefined })
      .then(setData)
      .catch((e) => setError(problemOf(e, "Could not load the statement")));
  }, [box, from, to]);

  const cur = box?.currency || "";
  const cell = { fontSize: 13.5, whiteSpace: "nowrap", py: 1 };

  const edge = (label, value) => (
    <TableRow>
      <TableCell colSpan={5} sx={{ ...cell, fontWeight: 700 }}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{ ...cell, fontWeight: 800 }}>
        {`${money(value)} ${cur}`}
      </TableCell>
    </TableRow>
  );

  return (
    <DialogShell
      open={Boolean(box)}
      title={`Statement · كشف حساب - ${box?.name || ""}`}
      onClose={onClose}
      error={error}
      maxWidth="lg"
    >
      <SuiBox mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <FilterDates label="Period" from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <SuiTypography variant="caption" sx={{ opacity: 0.7 }}>
          {data ? `${data.lines.length} movements` : ""}
        </SuiTypography>
      </SuiBox>

      {!data && !error ? (
        <CargoLoader label="Loading statement" />
      ) : (
        data && (
          <SuiBox sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>No.</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Party / Detail</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {edge(from ? `Opening balance on ${from}` : "Opening balance", data.opening)}
                {data.lines.map((l) => (
                  <TableRow key={`${l.kind}-${l.id}`}>
                    <TableCell sx={cell}>{l.date}</TableCell>
                    <TableCell sx={{ ...cell, fontWeight: 700 }}>{l.ref}</TableCell>
                    <TableCell sx={cell}>{KIND[l.kind] || l.kind}</TableCell>
                    <TableCell sx={{ ...cell, whiteSpace: "normal" }}>
                      {[l.party, l.detail].filter(Boolean).join(" · ") || "-"}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={({ palette }) => ({
                        ...cell,
                        fontWeight: 700,
                        color: l.amount >= 0 ? palette.success.main : palette.error.main,
                      })}
                    >
                      {`${l.amount >= 0 ? "+" : ""}${money(l.amount)}`}
                    </TableCell>
                    <TableCell align="right" sx={cell}>
                      {money(l.balance)}
                    </TableCell>
                  </TableRow>
                ))}
                {edge(to ? `Closing balance on ${to}` : "Closing balance", data.closing)}
              </TableBody>
            </Table>
          </SuiBox>
        )
      )}
    </DialogShell>
  );
}

StatementDialog.defaultProps = { box: null };
StatementDialog.propTypes = {
  box: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    currency: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

export default StatementDialog;
